import { type ReactNode } from 'react';

export interface DsSegmentedOption<T extends string | number> {
  value: T;
  label: ReactNode;
  /** Optional badge rendered after the label (e.g. result count). */
  count?: number;
}

interface DsSegmentedProps<T extends string | number> {
  options: DsSegmentedOption<T>[];
  /** Currently selected value; pass `null` to render with nothing pressed. */
  value: T | null;
  onChange: (value: T) => void;
  ariaLabel: string;
  size?: 'default' | 'small';
  className?: string;
}

/**
 * Segmented radio-group control: a row of joined buttons where exactly one is
 * pressed. Use for short, mutually-exclusive filters or modes (e.g. Alle /
 * Kunden / Lieferanten). For longer option lists use {@link DsTabNav} instead.
 */
export function DsSegmented<T extends string | number>({
  options,
  value,
  onChange,
  ariaLabel,
  size = 'default',
  className = '',
}: DsSegmentedProps<T>) {
  const classes = ['ds-segmented', size !== 'default' && size, className]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classes} role="radiogroup" aria-label={ariaLabel}>
      {options.map((opt) => {
        const selected = opt.value === value;
        return (
          <button
            key={String(opt.value)}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-pressed={selected}
            onClick={() => onChange(opt.value)}
          >
            <span className="ds-segmented-label">{opt.label}</span>
            {opt.count !== undefined && (
              <span className="ds-segmented-count">{opt.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
